import { trpc } from "@/common/utils/trpc";
import type React from "react";
import { useState, useEffect } from "react";
import { BrowserCacheStorage } from "./BrowserCacheStorage";
import { BrowserEncryptionKeyManager } from "./BrowserEncryptionKeyManager";
import { CacheStorageProvider } from "./CacheStorageContext";
import { CapacitorCacheStorage } from "./CapacitorCacheStorage";
import { CapacitorEncryptionKeyManager } from "./CapacitorEncryptionKeyManager";
import { EncryptedKeyValueStorage } from "./EncryptedKeyValueStorage";
import { usePreferenceStorage } from "./PreferenceStorageContext";
import { useGetCurrentUserQuery } from "../api/apiHooks";
import type { EncryptionKeyManager } from "./EncryptionKeyManager";
import { KeyValueStorageFallbackWrapper } from "./KeyValueStorageFallbackWrapper";

export function EncryptedCacheStorageProvider({
	children,
}: { children: React.ReactNode }) {
	if (import.meta.env.VITE_CAPACITOR_BUILD === "true") {
		const [storage] = useState(() => {
			return new EncryptedKeyValueStorage(
				new KeyValueStorageFallbackWrapper(
					new CapacitorCacheStorage("vcg-cache"),
					new BrowserCacheStorage("vcg-cache-fallback", "cache"),
				),
				new CapacitorEncryptionKeyManager(
					"vcg-encryption-key",
					new CapacitorCacheStorage("vcg-secure-storage-fallback"),
				),
			);
		});
		return (
			<CacheStorageProvider storage={storage}>{children}</CacheStorageProvider>
		);
	}

	const preferenceStorage = usePreferenceStorage();
	const trpcClient = trpc.useUtils().client;

	const [encryptionKeyManager] = useState(() => {
		return new BrowserEncryptionKeyManager(preferenceStorage, {
			createServerKey: () => trpcClient.encryptionKeys.createKey.mutate(),
			getServerKey: (id) =>
				trpcClient.encryptionKeys.getKey
					.query({ keyUuid: id })
					.then((res) => res.decryptedKey)
					.catch((e) => {
						if (
							e instanceof Error &&
							// if not found or failed to decrypt due to some key mismatch
							// return null so the manager will request a new key be created
							(e.message?.toLowerCase().includes("not found") ||
								e.message
									?.toLowerCase()
									.includes("failed to decrypt encryption key"))
						) {
							return null;
						}

						throw e;
					}),
		});
	});
	const [storage] = useState(() => {
		return new EncryptedKeyValueStorage(
			new BrowserCacheStorage("vcg-cache", "cache"),
			encryptionKeyManager,
		);
	});

	return (
		<CacheStorageProvider storage={storage}>
			<EncryptionKeyPrefetcher encryptionKeyManager={encryptionKeyManager} />
			{children}
		</CacheStorageProvider>
	);
}

function EncryptionKeyPrefetcher({
	encryptionKeyManager,
}: {
	encryptionKeyManager: EncryptionKeyManager;
}) {
	const currentUser = useGetCurrentUserQuery();

	useEffect(() => {
		if (currentUser.data) {
			// prefetch the encryption key as soon as possible to make sure it's available before the user goes offline.
			// only relevant for browser builds where the key is stored on the server.
			encryptionKeyManager.getKey();
		}
	}, [currentUser.data]);

	return null;
}
